import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Phone } from 'lucide-react';
import Logo from '../components/Logo';
import { Reveal } from '../components/hammer/reveal';
import { HAMMER_FAQ_ITEMS } from '../components/hammer/HammerFAQ';
import { CONTACT } from '../mock';

// Standalone /faq page — exists for search traffic. Reuses the landing page's
// HAMMER_FAQ_ITEMS as the single source of truth, plus page-only extras that
// make sense for searchers (kept grounded in owner-confirmed facts only).
const PAGE_FAQ_ITEMS = [
  ...HAMMER_FAQ_ITEMS,
  {
    q: 'How much does the Absolute Hammer weigh?',
    a: 'Crated for freight, the hammer weighs about 850 lbs — far over the limit for ground parcel carriers, so every hammer travels LTL freight. Use the freight estimator on our homepage for an instant price range to your ZIP code.',
  },
];

const TITLE = 'Pile Hammer FAQ — Absolute Hammer by Absolute Marine Equipment';
const DESCRIPTION =
  'Answers to common questions about the Absolute Hammer pneumatic pile driver: piling sizes up to 12", air compressor requirements, $10,500 pricing, warranty, nationwide LTL freight shipping, and more.';
const CANONICAL = 'https://absolutehammers.com/faq';

// Minimal head management (no react-helmet in this project). Sets title,
// description, canonical and FAQPage JSON-LD on mount; restores on unmount.
function useFaqSeo() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;

    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc ? metaDesc.getAttribute('content') : null;
    if (metaDesc) metaDesc.setAttribute('content', DESCRIPTION);

    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute('href') : null;
    if (canonical) canonical.setAttribute('href', CANONICAL);

    const jsonLd = document.createElement('script');
    jsonLd.type = 'application/ld+json';
    jsonLd.id = 'faq-page-jsonld';
    jsonLd.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: PAGE_FAQ_ITEMS.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    });
    document.head.appendChild(jsonLd);

    window.scrollTo(0, 0);

    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc) metaDesc.setAttribute('content', prevDesc);
      if (canonical && prevCanonical) canonical.setAttribute('href', prevCanonical);
      const el = document.getElementById('faq-page-jsonld');
      if (el) el.remove();
    };
  }, []);
}

export default function FaqPage() {
  useFaqSeo();

  return (
    <div className="App bg-black min-h-screen text-zinc-200" data-testid="faq-page">
      {/* Slim header (same pattern as the legal pages) */}
      <header className="border-b border-white/5 bg-black/90 backdrop-blur">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <Logo />
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs sm:text-sm text-zinc-400 hover:text-cyan-300 transition-colors"
            data-testid="faq-back-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 py-12 sm:py-16">
        <Reveal>
          <div className="mb-10 sm:mb-14">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 sm:w-10 bg-cyan-400" />
              <span className="font-tech text-cyan-300 text-[10px] sm:text-[11px] uppercase tracking-[0.28em]">
                Absolute Hammer
              </span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase tracking-tight leading-[1.05]">
              Pile hammer <span className="text-cyan-300">FAQ</span>
            </h1>
            <p className="mt-4 text-sm sm:text-base text-zinc-400 leading-relaxed max-w-2xl">
              Straight answers about the Absolute Hammer — the fully galvanized
              pneumatic pile-driving hammer we design, build, and use ourselves
              in Fort Myers, Florida. $10,500, in stock, shipping anywhere in
              the Lower 48.
            </p>
          </div>
        </Reveal>

        {/* All answers open — this page is for readers and search engines,
            no accordion hide-and-seek */}
        <div className="space-y-8 sm:space-y-10">
          {PAGE_FAQ_ITEMS.map((item, i) => (
            <Reveal key={item.q} delay={Math.min(i * 0.05, 0.4)} y={16}>
              <article data-testid={`faq-page-item-${i}`}>
                <h2 className="font-display text-white text-lg sm:text-xl font-semibold leading-snug tracking-tight">
                  {item.q}
                </h2>
                <p className="mt-2.5 text-zinc-400 text-sm sm:text-[15px] leading-relaxed max-w-3xl">
                  {item.a}
                </p>
              </article>
            </Reveal>
          ))}
        </div>

        {/* CTA */}
        <Reveal delay={0.1} y={16}>
          <div className="mt-14 sm:mt-16 rounded-xl border border-cyan-400/25 bg-cyan-400/[0.04] px-6 sm:px-8 py-7 sm:py-8">
            <h2 className="font-display text-white text-xl sm:text-2xl font-semibold tracking-tight">
              Still have a question?
            </h2>
            <p className="mt-2 text-sm text-zinc-400 leading-relaxed max-w-xl">
              Call us — we answer the phone. Or send the quote form and we will
              get back to you with pricing and a firm freight number for your
              ZIP.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <a
                href={CONTACT.salesPhone.tel}
                data-testid="faq-call-link"
                className="btn-teal inline-flex items-center gap-2 px-5 py-3 rounded-md text-xs sm:text-sm uppercase tracking-[0.16em] whitespace-nowrap"
              >
                <Phone className="w-4 h-4" /> {CONTACT.salesPhone.number}
              </a>
              <Link
                to="/#hammer-quote"
                data-testid="faq-quote-link"
                className="btn-ghost inline-flex items-center gap-2 px-5 py-3 rounded-md text-xs sm:text-sm uppercase tracking-[0.16em] whitespace-nowrap"
              >
                Request a quote <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </main>

      {/* Minimal footer */}
      <footer className="border-t border-white/5">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-xs text-zinc-500">
            © {new Date().getFullYear()} Group Absolute Builders LLC d/b/a Absolute Marine Equipment
          </div>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="text-xs text-zinc-500 hover:text-cyan-300 transition-colors" data-testid="faq-privacy-link">
              Privacy Policy
            </Link>
            <Link to="/sms-terms" className="text-xs text-zinc-500 hover:text-cyan-300 transition-colors" data-testid="faq-sms-terms-link">
              SMS Terms
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
